<script setup lang="ts">
import { matchesSearchAccentInsensitive } from '@/utils/string/search-normalize'
import { useField } from 'vee-validate'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
// Props
const props = withDefaults(defineProps<{
  name: string
  options?: Array<any>
  labelAttribute: string
  alreadySelectedOption?: object
  placeholder?: string
  hasAllOption?: boolean // Nova prop para ativar a opção "Todos"
  errorMessage?: string
}>(), {
  options: () => []
})

// Emits
const emits = defineEmits<{
  (e: 'option-selected', payload: object): void
  (e: 'all-selected'): void // Novo evento para "Todos"
}>()

// Reactive
const isOptionsExpanded = ref<boolean>(false)
const selectedOption = ref<any>({ [props.labelAttribute]: t('globals.dropdowns.default.placeholder') })

// Validation
const { value: inputValue, errorMessage } = useField<object>(props.name)
const optionName = ref<string>('')
const inputRef = ref<HTMLInputElement | null>(null)
// Helpers
function setOption(option: any) {
  const allLabel = t('globals.dropdowns.all')
  if (props.hasAllOption && option.id === null) {
    inputValue.value = null
    selectedOption.value = { id: null, [props.labelAttribute]: allLabel }
    optionName.value = allLabel
    emits('all-selected')
  } else {
    inputValue.value = option
    selectedOption.value = option
    optionName.value = option[props.labelAttribute] ?? option.name ?? ''
  }

  isOptionsExpanded.value = false
  emits('option-selected', option)
  inputRef.value?.blur()
}

function syncOptionNameFromValue() {
  const label = props.labelAttribute
  if (props.hasAllOption && (inputValue.value === null || inputValue.value === undefined)) {
    optionName.value = t('globals.dropdowns.all')
    return
  }
  if (inputValue.value && typeof inputValue.value === 'object') {
    optionName.value = String(inputValue.value[label] ?? inputValue.value.name ?? '')
    return
  }
  if (selectedOption.value && typeof selectedOption.value === 'object') {
    optionName.value = String(selectedOption.value[label] ?? selectedOption.value.name ?? '')
    return
  }
  optionName.value = ''
}

function handleFocus() {
  isOptionsExpanded.value = true
  optionName.value = ''
}

function handleBlur() {
  isOptionsExpanded.value = false
  syncOptionNameFromValue()
}

const filteredOptions = computed(() => {
  if (!Array.isArray(props.options)) {
    return []
  }
  const labelKey = props.labelAttribute
  return props.options.filter((option) => {
    const label = (option[labelKey] ?? option.name ?? '').toString()
    return matchesSearchAccentInsensitive(label, optionName.value)
  })
})

// Lifecycle
onMounted(() => {
  if (!props.alreadySelectedOption) return

  if (Object.keys(props.alreadySelectedOption ?? '').length !== 0) {
    setOption(props.alreadySelectedOption)
  }
})

watch(inputValue, (newValue) => {
  if (props.hasAllOption && (newValue === null || newValue === undefined)) {
    const allLabel = t('globals.dropdowns.all')
    selectedOption.value = { id: null, [props.labelAttribute]: allLabel }
    return
  }
  if (newValue !== selectedOption.value) {
    selectedOption.value = newValue
  }
})

watch(optionName, (newValue) => {
  if (newValue === '') return
  const label = props.labelAttribute
  const selectedLabel = selectedOption.value?.[label] ?? selectedOption.value?.name
  if (newValue !== selectedLabel) {
    selectedOption.value = null
    isOptionsExpanded.value = true
  }
})

watch(() => props.alreadySelectedOption, (newValue) => {
  if (newValue && Object.keys(newValue ?? '').length !== 0) {
    setOption(newValue)
  }
})
</script>
<template>
  <div class="relative w-full">
    <input
      ref="inputRef"
      v-model="optionName"
      type="text"
      class="w-full border border-zinc-300 bg-white py-3 pl-4 pr-10 transition-colors hover:border-zinc-400 focus:border-primary focus:outline-none"
      :placeholder="props.placeholder || t('placeholders.select')"
      @focus="handleFocus"
      @blur="handleBlur" />
    <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
      <svg
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        class="h-4 w-4 transform text-zinc-400 transition-transform duration-200 ease-in-out"
        :class="isOptionsExpanded ? 'rotate-180' : 'rotate-0'">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </div>

    <ul v-show="isOptionsExpanded" class="absolute left-0 right-0 z-10 mb-4 max-h-96 overflow-y-auto bg-white shadow-lg">
      <li
        v-if="props.hasAllOption"
        class="cursor-pointer bg-black-2 bg-green-500 px-3 py-2 text-white transition-colors duration-300 hover:bg-black"
        @mousedown.prevent="setOption({ id: null, [props.labelAttribute]: t('globals.dropdowns.all') })">
        Todos
      </li>
      <li
        v-for="(option, index) in filteredOptions"
        :key="option.id ?? index"
        class="cursor-pointer px-3 py-2 font-medium transition-colors duration-300 hover:bg-lightGray"
        @mousedown.prevent="setOption(option)">
        <slot name="option" :option="option" :label-attribute="props.labelAttribute">
          <span>{{ option[props.labelAttribute] ?? option.name }}</span>
        </slot>
      </li>
    </ul>

    <p v-if="props.errorMessage && !isOptionsExpanded" class="absolute text-title-xsm4 font-medium text-red">
      {{ props.errorMessage }}
    </p>
  </div>
</template>
